/**
 * One `applyFilters` pass per (catalog, filters) change, shared by the road
 * and the control panel through context.
 *
 * The road needs the surviving entries; the panel needs the facet counts and
 * the totals from the same pass. Computing it twice would be cheap but could
 * disagree, and "12 of 81" in the footer disagreeing with the list above it is
 * exactly the kind of bug nobody reports and everybody notices.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useFilters } from "@/state/filters.ts";
import { applyFilters, type FilterOutcome } from "./apply.ts";

const FilteredRoadContext = createContext<FilterOutcome | null>(null);

export function FilteredRoadProvider({ children }: { children: ReactNode }) {
  const catalog = useCatalog();
  const filters = useFilters();
  const outcome = useMemo(() => applyFilters(catalog, filters), [catalog, filters]);
  return <FilteredRoadContext.Provider value={outcome}>{children}</FilteredRoadContext.Provider>;
}

export function useFilteredRoad(): FilterOutcome {
  const outcome = useContext(FilteredRoadContext);
  if (!outcome) throw new Error("useFilteredRoad must be used inside FilteredRoadProvider");
  return outcome;
}
